import React, { useState } from "react";
import {
    Box,
    Button,
    Drawer,
    Typography,
    Chip,
    TextField,
    alpha,
    InputAdornment,
    useTheme,
    Divider,
} from "@mui/material";
import BorderedBox from "@convin/components/custom_components/BorderedBox";
import { CloseSvg } from "@convin/components/svg";
import VirtualList from "@convin/components/custom_components/VirtualList/VirtualList";
import SearchSvg from "@convin/components/svg/SearchSvg";

interface DataTagsProps {
    items: string[];
    maxItems?: number;
    itemComponent?: (item: string, index: number) => React.ReactNode;
    drawerTitle?: React.ReactNode;
    drawerComponent?: (items: string[]) => React.ReactNode;
}

const DataTags: React.FC<DataTagsProps> = ({
    items,
    maxItems = 4,
    itemComponent,
    drawerTitle = "All Items",
    drawerComponent,
}) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const theme = useTheme();

    // Default item renderer
    const defaultItemRenderer = (item: string, index: number) => (
        <Chip
            key={index}
            label={item}
            variant="filled"
            sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontSize: theme.typography.pxToRem(14),
            }}
        />
    );
    const defaultDrawerComponent = (items: string[]) => (
        <VirtualList<{ label: string; id: string }>
            data={items.map((item) => ({ label: item, id: item }))}
            Component={({ data, index }) => (
                <BorderedBox
                    key={index}
                    py={1.75}
                    px={2.75}
                    sx={{
                        border: "1px solid rgba(153, 153, 153, 0.20)",
                        borderRadius: "4px",
                        boxShadow: "none",
                        mt: 1,
                    }}
                >
                    <Typography variant="small" fontWeight={600}>
                        {data.label}
                    </Typography>
                </BorderedBox>
            )}
            rowSize={40}
            sx={{
                p: 0,
            }}
        />
    );
    const visibleItems = items.slice(0, maxItems);
    const hiddenItems = items.length > maxItems ? items.slice(maxItems) : [];
    const renderItem = itemComponent || defaultItemRenderer;
    const renderDrawerComponent = drawerComponent || defaultDrawerComponent;

    if (items.length === 0) {
        return <>-</>;
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    overflow: "hidden",
                }}
            >
                {visibleItems.map((item, index) => (
                    <>{renderItem(item, index)}</>
                ))}
                {hiddenItems.length > 0 && (
                    <Chip
                        label={`+${hiddenItems.length}`}
                        variant="outlined"
                        component="button"
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.1),
                            color: "primary.main",
                            fontSize: theme.typography.pxToRem(14),
                        }}
                    />
                )}
            </Box>
            <Drawer
                anchor="right"
                open={drawerOpen}
                sx={{
                    zIndex: 10000,
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 500,
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        p={3}
                        className="flex justify-between items-center flex-shrink-0"
                    >
                        <Typography variant="h6">{drawerTitle}</Typography>
                        <Button
                            variant="text"
                            sx={{
                                color: "textColors.333",
                            }}
                            onClick={() => setDrawerOpen(false)}
                        >
                            <CloseSvg />
                        </Button>
                    </Box>
                    <Divider />
                    <Box
                        p={1.5}
                        className="flex-1 overflow-hidden flex flex-col"
                    >
                        <div className="flex-shrink-0">
                            <TextField
                                fullWidth
                                variant="standard"
                                placeholder="Search"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchSvg
                                                sx={{
                                                    color: "textColors.666",
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                    disableUnderline: true,
                                }}
                                sx={(theme) => ({
                                    px: 2.75,
                                    py: 1,
                                    borderRadius: theme.spacing(0.5),
                                    border: `1px solid ${alpha(
                                        theme.palette.textColors[999],
                                        0.2
                                    )}`,
                                })}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        {renderDrawerComponent(
                            items.filter((item) =>
                                item
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase())
                            )
                        )}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default DataTags;
